import React, { useState, useEffect } from "react";
import NavBar from "./NavBar";
import Footer from "./Footer";
import SEO from "./SEO";
import { motion } from "framer-motion";
import {
  Users,
  Shield,
  Zap,
  TrendingUp,
  Lock,
  Calendar,
  FileText,
  MessageSquare,
  CheckCircle,
  ArrowRight,
  BarChart3,
  Settings,
  UserPlus,
  FolderOpen,
  Clock,
  Star,
  Globe,
  ChevronRight,
  Building2,
} from "lucide-react";
import googleLogo from "../assets/images/logos/google.svg";
import microsoftLogo from "../assets/images/logos/microsoft.svg";
import slackLogo from "../assets/images/logos/slack.svg";
import githubLogo from "../assets/images/logos/github.svg";
import jiraLogo from "../assets/images/logos/jira.svg";
import trelloLogo from "../assets/images/logos/trello.svg";

const TeamsPage = () => {
  const [darkMode] = useState(false);
  const [logoFade] = useState(false);
  const [annualBilling, setAnnualBilling] = useState(true);
  const [teamUsers, setTeamUsers] = useState(5);

  useEffect(() => {
    document.body.classList.toggle("dark", darkMode);
  }, [darkMode]);

  const features = [
    {
      icon: Users,
      title: "Shared Workspaces",
      description:
        "Create team workspaces where everyone can collaborate on tasks, projects, and documents in real-time.",
      gradient: "from-blue-500 to-cyan-500",
    },
    {
      icon: FolderOpen,
      title: "Unlimited Team Storage",
      description:
        "Store and share files with your team. No limits on storage - keep all your team's documents in one place.",
      gradient: "from-purple-500 to-pink-500",
    },
    {
      icon: Settings,
      title: "Admin Controls & Roles",
      description:
        "Manage team members with granular role-based permissions. Owners and admins can control access and settings.",
      gradient: "from-orange-500 to-red-500",
    },
    {
      icon: BarChart3,
      title: "Team Analytics",
      description:
        "Get insights into team productivity, task completion rates, and time tracking across all members.",
      gradient: "from-green-500 to-emerald-500",
    },
    {
      icon: Shield,
      title: "Enhanced Security",
      description:
        "Advanced security features including audit logs, custom permissions, and team-wide security policies.",
      gradient: "from-indigo-500 to-blue-500",
    },
    {
      icon: Calendar,
      title: "Shared Calendars",
      description:
        "Team calendars that sync across all members. Schedule meetings, track deadlines, and coordinate effortlessly.",
      gradient: "from-pink-500 to-rose-500",
    },
    {
      icon: MessageSquare,
      title: "Team Rooms & Chat",
      description:
        "Built-in team communication with channels, threads, and real-time messaging. Stay connected without switching apps.",
      gradient: "from-cyan-500 to-teal-500",
    },
    {
      icon: UserPlus,
      title: "Easy Team Invites",
      description:
        "Invite team members via email or shareable links. Onboard new members quickly with team-specific onboarding.",
      gradient: "from-yellow-500 to-orange-500",
    },
    {
      icon: FileText,
      title: "Collaborative Docs",
      description:
        "Real-time collaborative editing on documents, spreadsheets, and slides. Multiple team members can work together.",
      gradient: "from-violet-500 to-purple-500",
    },
    {
      icon: Zap,
      title: "Team Integrations",
      description:
        "Connect your team's favorite tools - Slack, GitHub, Jira, and more. Integrate once, everyone benefits.",
      gradient: "from-red-500 to-pink-500",
    },
    {
      icon: Clock,
      title: "Team Time Tracking",
      description:
        "Track time across team projects. See who's working on what and generate reports for billing or productivity.",
      gradient: "from-blue-500 to-indigo-500",
    },
    {
      icon: Lock,
      title: "Custom Permissions",
      description:
        "Fine-grained control over what each team member can see and do. Protect sensitive information with role-based access.",
      gradient: "from-emerald-500 to-green-500",
    },
  ];

  const benefits = [
    {
      icon: TrendingUp,
      title: "Boost Team Productivity",
      description:
        "Teams using Boxia report 40% increase in productivity by consolidating their tools into one platform.",
      stat: "40%",
      statLabel: "Productivity Increase",
    },
    {
      icon: Globe,
      title: "Perfect for Remote Teams",
      description:
        "Built-in features for distributed teams including timezone support, async communication, and global collaboration.",
      stat: "24/7",
      statLabel: "Global Collaboration",
    },
    {
      icon: Star,
      title: "Replace Multiple Tools",
      description:
        "One platform replaces task managers, calendars, docs, chat, and file storage saving time and money.",
      stat: "5+",
      statLabel: "Tools Replaced",
    },
  ];

  const useCases = [
    {
      title: "Startups",
      description:
        "Fast-growing startups need a flexible, all-in-one platform that scales with them. Start with a small team and expand as you grow.",
      icon: Building2,
      gradient: "from-blue-500 to-cyan-500",
      features: ["Quick setup", "Affordable pricing", "Scales with growth"],
    },
    {
      title: "Remote Teams",
      description:
        "Distributed teams need seamless collaboration tools. Boxia keeps everyone connected regardless of location or timezone.",
      icon: () => (
        <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-6 h-6">
          <circle cx="12" cy="12" r="9" fill="currentColor" opacity="0.2"/>
          <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="2"/>
          <path d="M12 3a15.3 15.3 0 0 1 4 9 15.3 15.3 0 0 1-4 9 15.3 15.3 0 0 1-4-9 15.3 15.3 0 0 1 4-9z" stroke="currentColor" strokeWidth="2"/>
          <path d="M3 12h18" stroke="currentColor" strokeWidth="2"/>
          <path d="M5 8h14M5 16h14" stroke="currentColor" strokeWidth="1.5" opacity="0.5"/>
        </svg>
      ),
      gradient: "from-purple-500 to-pink-500",
      features: ["Async communication", "Timezone support", "Video calls"],
    },
    {
      title: "Engineering Teams",
      description:
        "Development teams benefit from GitHub integration, sprint planning, and technical documentation all in one place. Build better software, faster.",
      icon: () => (
        <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-6 h-6">
          <rect x="3" y="3" width="18" height="18" rx="2" fill="currentColor" opacity="0.1"/>
          <path d="M8 8L11 11L8 14" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          <path d="M13 14h3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          <rect x="3" y="3" width="18" height="18" rx="2" stroke="currentColor" strokeWidth="2"/>
        </svg>
      ),
      gradient: "from-orange-500 to-red-500",
      features: ["GitHub integration", "Sprint planning", "Documentation"],
    },
    {
      title: "Creative Teams",
      description:
        "Design and marketing teams can collaborate on campaigns, manage assets, and keep creative projects on track with visual boards and feedback tools.",
      icon: () => (
        <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-6 h-6">
          <path d="M12 2C6.5 2 2 6.5 2 12s4.5 10 10 10c.926 0 1.648-.746 1.648-1.688 0-.437-.18-.835-.437-1.125-.29-.289-.438-.652-.438-1.125 0-.875.688-1.562 1.562-1.562h1.875c3.305 0 5.938-2.633 5.938-5.938C22 6.5 17.5 2 12 2z" fill="currentColor" opacity="0.2"/>
          <circle cx="7.5" cy="10.5" r="1.5" fill="currentColor"/>
          <circle cx="12" cy="7.5" r="1.5" fill="currentColor"/>
          <circle cx="16.5" cy="10.5" r="1.5" fill="currentColor"/>
          <circle cx="15" cy="15.5" r="1.5" fill="currentColor"/>
          <path d="M12 2C6.5 2 2 6.5 2 12s4.5 10 10 10c.926 0 1.648-.746 1.648-1.688 0-.437-.18-.835-.437-1.125-.29-.289-.438-.652-.438-1.125 0-.875.688-1.562 1.562-1.562h1.875c3.305 0 5.938-2.633 5.938-5.938C22 6.5 17.5 2 12 2z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      ),
      gradient: "from-emerald-500 to-teal-500",
      features: ["Asset management", "Campaign planning", "Design feedback"],
    },
  ];

  const integrations = [
    { name: "Google", logo: googleLogo, description: "Calendar & Drive" },
    { name: "Microsoft", logo: microsoftLogo, description: "Teams & 365" },
    { name: "Slack", logo: slackLogo, description: "Team messaging" },
    { name: "GitHub", logo: githubLogo, description: "Code repos" },
    { name: "Jira", logo: jiraLogo, description: "Issue tracking" },
    { name: "Trello", logo: trelloLogo, description: "Kanban boards" },
  ];

  const testimonials = [
    {
      quote:
        "Honestly, just being able to search across tasks, docs, and chat in one place has been huge. We waste way less time hunting for stuff.",
      author: "Sarah M.",
      role: "Design Lead at Forge Studio",
      avatar:
        "https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=150&h=150&fit=crop",
    },
    {
      quote:
        "We switched from juggling Asana, Notion, and Slack. It's not perfect, but having one less tab open is honestly a win for my brain.",
      author: "Alex K.",
      role: "Engineering Lead at Streamline",
      avatar:
        "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=150&h=150&fit=crop",
    },
    {
      quote:
        "Our team is split between SF and Berlin. The time zone awareness and async updates mean I'm not waking up to 50 Slack messages anymore.",
      author: "Jordan L.",
      role: "Operations Manager at Tidepool",
      avatar:
        "https://images.unsplash.com/photo-1506794778202-cad84cf45f1d?w=150&h=150&fit=crop",
    },
  ];

  // Core features highlighted in the bento layout
  const bento = {
    workspaces: features[0], // Shared Workspaces
    security: features[4], // Enhanced Security
    storage: features[1], // Unlimited Team Storage
    admin: features[2], // Admin Controls & Roles
  };

  const calculateTeamCost = () => {
    const monthlyPrice = 12.99;
    const price = annualBilling ? monthlyPrice * 12 : monthlyPrice;
    return (price * teamUsers).toFixed(2);
  };

  const calculateSavings = () => {
    return "0.00";
  };

  return (
    <div
      className={`flex flex-col transition-colors duration-300 ${
        darkMode ? "dark bg-[#1a1a1a] text-white" : "bg-white text-gray-800"
      }`}
    >
      <SEO
        title="Boxia for Teams - All-in-One Productivity for Growing Teams"
        description="Empower your team with Boxia - the all-in-one productivity platform featuring shared workspaces, team analytics, admin controls, and unlimited collaboration tools."
        keywords="team productivity, collaboration software, project management, team workspace, remote team tools, startup productivity, team calendar, shared workspace"
        ogImage="https://boxia.io/og-teams.jpg"
        canonical="https://boxia.io/teams"
      />

      <main className="flex-grow overflow-y-auto">
        <NavBar darkMode={darkMode} logoFade={logoFade} />

        {/* Hero Section */}
        <section className="relative">
          <div className="absolute inset-0 z-[0] bg-purple-950/10 dark:bg-purple-950/10 bg-[radial-gradient(ellipse_20%_80%_at_50%_-20%,rgba(120,119,198,0.15),rgba(255,255,255,0))] dark:bg-[radial-gradient(ellipse_20%_80%_at_50%_-20%,rgba(120,119,198,0.3),rgba(255,255,255,0))]" />

          <div className="relative max-w-screen-xl z-10 mx-auto px-4 pt-28 pb-20 gap-12 md:px-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="space-y-5 max-w-3xl mx-auto text-center"
            >
              <h1 className="text-sm text-gray-600 dark:text-gray-400 group font-geist mx-auto px-5 py-2 bg-gradient-to-tr from-zinc-300/20 via-gray-400/20 to-transparent dark:from-zinc-300/5 dark:via-gray-400/5 border-[2px] border-black/5 dark:border-white/5 rounded-3xl w-fit">
                Built for Teams of All Sizes
                <ChevronRight className="inline w-4 h-4 ml-2 group-hover:translate-x-1 duration-300" />
              </h1>

              <h2 className="text-4xl tracking-tighter font-geist bg-clip-text text-transparent mx-auto md:text-6xl bg-[linear-gradient(180deg,_#000_0%,_rgba(0,_0,_0,_0.75)_100%)] dark:bg-[linear-gradient(180deg,_#FFF_0%,_rgba(255,_255,_255,_0.00)_202.08%)]">
                All-in-One Productivity{" "}
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-purple-600 to-pink-500 dark:from-purple-300 dark:to-orange-200">
                  for Your Team
                </span>
              </h2>

              <p className="max-w-2xl mx-auto text-gray-600 dark:text-gray-300">
                Empower your team with shared workspaces, real-time
                collaboration, admin controls, and team analytics - all in one
                beautiful platform.
              </p>

              <div className="items-center justify-center gap-x-3 space-y-3 sm:flex sm:space-y-0">
                <span className="relative inline-block overflow-hidden rounded-full p-[1.5px]">
                  <span className="absolute inset-[-1000%] animate-[spin_2s_linear_infinite] bg-[conic-gradient(from_90deg_at_50%_50%,#E2CBFF_0%,#393BB2_50%,#E2CBFF_100%)]" />
                  <div className="inline-flex h-full w-full cursor-pointer items-center justify-center rounded-full bg-white dark:bg-gray-950 text-xs font-medium backdrop-blur-3xl">
                    <a
                      href="https://app.boxia.io/auth/signup/team"
                      className="inline-flex rounded-full text-center group items-center w-full justify-center bg-gradient-to-tr from-zinc-300/20 via-purple-400/30 to-transparent dark:from-zinc-300/5 dark:via-purple-400/20 text-gray-900 dark:text-white border-input border-[1px] hover:bg-gradient-to-tr hover:from-zinc-300/30 hover:via-purple-400/40 hover:to-transparent dark:hover:from-zinc-300/10 dark:hover:via-purple-400/30 transition-all sm:w-auto py-4 px-10"
                    >
                      Start Free Team Trial
                    </a>
                  </div>
                </span>
              </div>

              <p className="text-sm text-gray-500 dark:text-gray-400">
                14 day free trial • Minimum 3 users
              </p>
            </motion.div>
          </div>
        </section>

        {/* Team Features Grid */}
        <section className="relative">
          <div className="absolute inset-0 z-[0] bg-purple-950/10 dark:bg-purple-950/10 bg-[radial-gradient(ellipse_20%_80%_at_50%_-20%,rgba(120,119,198,0.15),rgba(255,255,255,0))] dark:bg-[radial-gradient(ellipse_20%_80%_at_50%_-20%,rgba(120,119,198,0.3),rgba(255,255,255,0))]" />
          <div className="relative max-w-screen-xl z-10 mx-auto px-4 py-28 gap-12 md:px-8">
            <div className="space-y-5 max-w-3xl mx-auto text-center">
              <h1 className="text-sm text-gray-600 dark:text-gray-400 group font-geist mx-auto px-5 py-2 bg-gradient-to-tr from-zinc-300/20 via-gray-400/20 to-transparent dark:from-zinc-300/5 dark:via-gray-400/5 border-[2px] border-black/5 dark:border-white/5 rounded-xl w-fit">
                Team Features
                <ChevronRight className="inline w-4 h-4 ml-2 group-hover:translate-x-1 duration-300" />
              </h1>
              <h2 className="text-4xl tracking-tighter font-geist bg-clip-text text-transparent mx-auto md:text-6xl bg-[linear-gradient(180deg,_#000_0%,_rgba(0,_0,_0,_0.75)_100%)] dark:bg-[linear-gradient(180deg,_#FFF_0%,_rgba(255,_255,_255,_0.00)_202.08%)] pb-2">
                Everything Your Team{" "}
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-purple-600 to-pink-500 dark:from-purple-300 dark:to-orange-200">
                  Needs
                </span>
              </h2>
              <p className="max-w-2xl mx-auto text-gray-600 dark:text-gray-300">
                Built-in features designed specifically for team collaboration
                and productivity
              </p>
            </div>

            {/* Bento Feature Grid */}
            <div className="mt-16 grid grid-cols-1 md:grid-cols-4 gap-4 md:gap-6 auto-rows-[240px]">
              {/* Large Primary Tile */}
              {bento.workspaces && (
                <motion.div
                  key={bento.workspaces.title}
                  initial={{ opacity: 0 }}
                  whileInView={{ opacity: 1 }}
                  transition={{ duration: 0.6 }}
                  viewport={{ once: true }}
                  className="group relative flex flex-col md:col-span-2 md:row-span-2 p-8 md:p-10 rounded-3xl bg-gradient-to-br from-blue-50/80 via-purple-50/60 to-pink-50/40 dark:from-blue-950/30 dark:via-purple-950/20 dark:to-pink-950/10 border border-blue-100/60 dark:border-blue-900/30 hover:border-blue-200/80 dark:hover:border-blue-800/40 shadow-[inset_0_2px_4px_0_rgba(255,255,255,0.25),0_4px_12px_-4px_rgba(0,0,0,0.1)] dark:shadow-[inset_0_2px_4px_0_rgba(255,255,255,0.05),0_4px_12px_-4px_rgba(0,0,0,0.5)] transition-all duration-300 overflow-hidden"
                >
                  {/* Decorative gradient overlay */}
                  <div className="absolute inset-0 opacity-30 dark:opacity-20 bg-gradient-to-br from-blue-400/10 via-purple-400/5 to-transparent pointer-events-none" />

                  <div className="relative z-10 flex flex-col h-full">
                    <div
                      className={`inline-flex w-fit p-4 rounded-2xl bg-gradient-to-br ${bento.workspaces.gradient} mb-6 shadow-lg`}
                    >
                      <bento.workspaces.icon className="w-8 h-8 text-white" />
                    </div>

                    <h3 className="text-3xl font-bold mb-3 text-gray-900 dark:text-white tracking-tight">
                      {bento.workspaces.title}
                    </h3>

                    <p className="text-gray-700 dark:text-gray-300 text-base leading-relaxed mb-4">
                      Stop paying for Notion, Asana, Slack, Google Workspace,
                      and Dropbox separately. One workspace gives your team
                      everything: tasks, docs, chat, files, calendars, and more,
                      all working together seamlessly.
                    </p>

                    <p className="text-gray-600 dark:text-gray-400 text-sm leading-relaxed mb-6">
                      No more context switching between apps or hunting for
                      information across platforms. Your team's entire workflow
                      lives in one place, with powerful search, real-time sync,
                      and intelligent connections between tasks, documents, and
                      conversations.
                    </p>

                    <ul className="mt-auto space-y-3">
                      <li className="flex items-center gap-3 text-sm font-medium text-gray-800 dark:text-gray-200">
                        <CheckCircle className="w-5 h-5 text-green-500 dark:text-green-400 flex-shrink-0" />
                        Replace 5+ tools and save $500+/month per team
                      </li>
                      <li className="flex items-center gap-3 text-sm font-medium text-gray-800 dark:text-gray-200">
                        <CheckCircle className="w-5 h-5 text-green-500 dark:text-green-400 flex-shrink-0" />
                        Set up in 10 minutes instead of days
                      </li>
                      <li className="flex items-center gap-3 text-sm font-medium text-gray-800 dark:text-gray-200">
                        <CheckCircle className="w-5 h-5 text-green-500 dark:text-green-400 flex-shrink-0" />
                        Works offline so your team stays productive
                      </li>
                    </ul>
                  </div>
                </motion.div>
              )}

              {/* Enhanced Security */}
              {bento.security && (
                <motion.div
                  key={bento.security.title}
                  initial={{ opacity: 0 }}
                  whileInView={{ opacity: 1 }}
                  transition={{ duration: 0.6, delay: 0.1 }}
                  viewport={{ once: true }}
                  className="group relative p-6 md:col-span-2 rounded-2xl bg-gradient-to-br from-indigo-50/80 via-blue-50/60 to-cyan-50/40 dark:from-indigo-950/30 dark:via-blue-950/20 dark:to-cyan-950/10 border border-indigo-100/60 dark:border-indigo-900/30 hover:border-indigo-200/80 dark:hover:border-indigo-800/40 shadow-[inset_0_2px_4px_0_rgba(255,255,255,0.25),0_4px_12px_-4px_rgba(0,0,0,0.1)] dark:shadow-[inset_0_2px_4px_0_rgba(255,255,255,0.05),0_4px_12px_-4px_rgba(0,0,0,0.5)] transition-all duration-300 overflow-hidden"
                >
                  <div className="absolute inset-0 opacity-30 dark:opacity-20 bg-gradient-to-br from-indigo-400/10 via-blue-400/5 to-transparent pointer-events-none" />

                  <div className="relative z-10">
                    <div
                      className={`inline-flex w-fit p-3 rounded-xl bg-gradient-to-br ${bento.security.gradient} mb-4 shadow-lg`}
                    >
                      <bento.security.icon className="w-6 h-6 text-white" />
                    </div>
                    <h3 className="text-xl font-bold mb-2 text-gray-900 dark:text-white">
                      {bento.security.title}
                    </h3>
                    <p className="text-gray-700 dark:text-gray-300 text-sm leading-relaxed">
                      Security built for peace of mind. SOC 2 compliance, audit
                      logs, 2FA, and SSO come standard, so security
                      questionnaires are never a blocker. End to end encryption
                      keeps all your team data safe.
                    </p>
                  </div>
                </motion.div>
              )}

              {/* Unlimited Storage */}
              {bento.storage && (
                <motion.div
                  key={bento.storage.title}
                  initial={{ opacity: 0 }}
                  whileInView={{ opacity: 1 }}
                  transition={{ duration: 0.6, delay: 0.15 }}
                  viewport={{ once: true }}
                  className="group relative p-6 rounded-2xl bg-gradient-to-br from-purple-50/80 via-pink-50/60 to-rose-50/40 dark:from-purple-950/30 dark:via-pink-950/20 dark:to-rose-950/10 border border-purple-100/60 dark:border-purple-900/30 hover:border-purple-200/80 dark:hover:border-purple-800/40 shadow-[inset_0_2px_4px_0_rgba(255,255,255,0.25),0_4px_12px_-4px_rgba(0,0,0,0.1)] dark:shadow-[inset_0_2px_4px_0_rgba(255,255,255,0.05),0_4px_12px_-4px_rgba(0,0,0,0.5)] transition-all duration-300 overflow-hidden"
                >
                  <div className="absolute inset-0 opacity-30 dark:opacity-20 bg-gradient-to-br from-purple-400/10 via-pink-400/5 to-transparent pointer-events-none" />

                  <div className="relative z-10">
                    <div
                      className={`inline-flex w-fit p-3 rounded-xl bg-gradient-to-br ${bento.storage.gradient} mb-4 shadow-lg`}
                    >
                      <bento.storage.icon className="w-6 h-6 text-white" />
                    </div>
                    <h3 className="text-xl font-bold mb-2 text-gray-900 dark:text-white">
                      {bento.storage.title}
                    </h3>
                    <p className="text-gray-700 dark:text-gray-300 text-sm leading-relaxed">
                      No more "can you resend that file?" or digging through
                      Slack for attachments. Everything in one place, fully
                      unlimited.
                    </p>
                  </div>
                </motion.div>
              )}

              {/* Admin Controls */}
              {bento.admin && (
                <motion.div
                  key={bento.admin.title}
                  initial={{ opacity: 0 }}
                  whileInView={{ opacity: 1 }}
                  transition={{ duration: 0.6, delay: 0.2 }}
                  viewport={{ once: true }}
                  className="group relative p-6 rounded-2xl bg-gradient-to-br from-orange-50/80 via-amber-50/60 to-yellow-50/40 dark:from-orange-950/30 dark:via-amber-950/20 dark:to-yellow-950/10 border border-orange-100/60 dark:border-orange-900/30 hover:border-orange-200/80 dark:hover:border-orange-800/40 shadow-[inset_0_2px_4px_0_rgba(255,255,255,0.25),0_4px_12px_-4px_rgba(0,0,0,0.1)] dark:shadow-[inset_0_2px_4px_0_rgba(255,255,255,0.05),0_4px_12px_-4px_rgba(0,0,0,0.5)] transition-all duration-300 overflow-hidden"
                >
                  <div className="absolute inset-0 opacity-30 dark:opacity-20 bg-gradient-to-br from-orange-400/10 via-amber-400/5 to-transparent pointer-events-none" />

                  <div className="relative z-10">
                    <div
                      className={`inline-flex w-fit p-3 rounded-xl bg-gradient-to-br ${bento.admin.gradient} mb-4 shadow-lg`}
                    >
                      <bento.admin.icon className="w-6 h-6 text-white" />
                    </div>
                    <h3 className="text-xl font-bold mb-2 text-gray-900 dark:text-white">
                      {bento.admin.title}
                    </h3>
                    <p className="text-gray-700 dark:text-gray-300 text-sm leading-relaxed">
                      Flexible roles and permissions let you control access
                      across your team. Add members, set up guest access, and
                      adjust permissions.
                    </p>
                  </div>
                </motion.div>
              )}
            </div>
          </div>
        </section>

        {/* Benefits Section */}
        <section className="relative">
          <div className="absolute inset-0 z-[0] bg-purple-950/10 dark:bg-purple-950/10 bg-[radial-gradient(ellipse_20%_80%_at_50%_-20%,rgba(120,119,198,0.15),rgba(255,255,255,0))] dark:bg-[radial-gradient(ellipse_20%_80%_at_50%_-20%,rgba(120,119,198,0.3),rgba(255,255,255,0))]" />
          <div className="relative max-w-screen-xl z-10 mx-auto px-4 py-28 gap-12 md:px-8">
            <div className="space-y-5 max-w-3xl mx-auto text-center mb-16">
              <h1 className="text-sm text-gray-600 dark:text-gray-400 group font-geist mx-auto px-5 py-2 bg-gradient-to-tr from-zinc-300/20 via-gray-400/20 to-transparent dark:from-zinc-300/5 dark:via-gray-400/5 border-[2px] border-black/5 dark:border-white/5 rounded-xl w-fit">
                Why Boxia
                <ChevronRight className="inline w-4 h-4 ml-2 group-hover:translate-x-1 duration-300" />
              </h1>
              <h2 className="text-4xl pb-2 tracking-tighter font-geist bg-clip-text text-transparent mx-auto md:text-6xl bg-[linear-gradient(180deg,_#000_0%,_rgba(0,_0,_0,_0.75)_100%)] dark:bg-[linear-gradient(180deg,_#FFF_0%,_rgba(255,_255,_255,_0.00)_202.08%)]">
                Why Teams{" "}
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-purple-600 to-pink-500 dark:from-purple-300 dark:to-orange-200">
                  Choose Boxia
                </span>
              </h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {benefits.map((benefit, index) => (
                <motion.div
                  key={benefit.title}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  viewport={{ once: true }}
                  className="group relative overflow-hidden rounded-xl border-[1px] border-black/5 dark:border-white/5 bg-gradient-to-tr from-zinc-300/20 via-gray-400/20 to-transparent dark:from-zinc-300/5 dark:via-gray-400/5 p-8 transition-all duration-300 hover:shadow-xl"
                >
                  <div className="absolute inset-0 bg-gradient-to-r from-purple-600/10 to-pink-500/10 dark:from-purple-300/10 dark:to-orange-200/10 opacity-0 transition-opacity duration-300 group-hover:opacity-100" />

                  <div className="relative space-y-4">
                    {/* Icon */}
                    <div className="inline-block rounded-lg bg-gradient-to-tr from-zinc-300/30 via-purple-400/30 to-transparent dark:from-zinc-300/10 dark:via-purple-400/20 p-3">
                      <benefit.icon className="w-6 h-6 text-gray-900 dark:text-gray-100" />
                    </div>

                    {/* Stat - Large and prominent */}
                    <div className="space-y-1">
                      <div className="text-5xl font-bold bg-gradient-to-r from-purple-600 to-pink-500 dark:from-purple-300 dark:to-orange-200 bg-clip-text text-transparent tracking-tight">
                        {benefit.stat}
                      </div>
                      <div className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                        {benefit.statLabel}
                      </div>
                    </div>

                    {/* Title */}
                    <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 pt-2">
                      {benefit.title}
                    </h3>

                    {/* Description */}
                    <p className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed">
                      {benefit.description}
                    </p>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Use Cases */}
        <section className="relative">
          <div className="absolute inset-0 z-[0] bg-purple-950/10 dark:bg-purple-950/10 bg-[radial-gradient(ellipse_20%_80%_at_50%_-20%,rgba(120,119,198,0.15),rgba(255,255,255,0))] dark:bg-[radial-gradient(ellipse_20%_80%_at_50%_-20%,rgba(120,119,198,0.3),rgba(255,255,255,0))]" />
          <div className="relative max-w-screen-xl z-10 mx-auto px-4 py-28 gap-12 md:px-8">
            <div className="space-y-5 max-w-3xl mx-auto text-center mb-16">
              <h1 className="text-sm text-gray-600 dark:text-gray-400 group font-geist mx-auto px-5 py-2 bg-gradient-to-tr from-zinc-300/20 via-gray-400/20 to-transparent dark:from-zinc-300/5 dark:via-gray-400/5 border-[2px] border-black/5 dark:border-white/5 rounded-xl w-fit">
                Use Cases
                <ChevronRight className="inline w-4 h-4 ml-2 group-hover:translate-x-1 duration-300" />
              </h1>
              <h2 className="text-4xl tracking-tighter font-geist bg-clip-text text-transparent mx-auto md:text-6xl bg-[linear-gradient(180deg,_#000_0%,_rgba(0,_0,_0,_0.75)_100%)] dark:bg-[linear-gradient(180deg,_#FFF_0%,_rgba(255,_255,_255,_0.00)_202.08%)]">
                Perfect for{" "}
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-purple-600 to-pink-500 dark:from-purple-300 dark:to-orange-200">
                  Every Team Type
                </span>
              </h2>
              <p className="max-w-2xl mx-auto text-gray-600 dark:text-gray-300">
                Whether you're a startup, remote team, agency, or dev team -
                Boxia adapts to your needs
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {useCases.map((useCase, index) => (
                <motion.div
                  key={useCase.title}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  viewport={{ once: true }}
                  className="group relative overflow-hidden rounded-xl border-[1px] border-black/5 dark:border-white/5 bg-gradient-to-tr from-zinc-300/20 via-gray-400/20 to-transparent dark:from-zinc-300/5 dark:via-gray-400/5 p-8 transition-all duration-300 hover:shadow-xl"
                >
                  <div className="absolute inset-0 bg-gradient-to-r from-purple-600/10 to-pink-500/10 dark:from-purple-300/10 dark:to-orange-200/10 opacity-0 transition-opacity duration-300 group-hover:opacity-100" />

                  <div className="relative space-y-4">
                    {/* Icon */}
                    <div
                      className={`inline-flex w-fit p-3 rounded-lg bg-gradient-to-br ${useCase.gradient} mb-2 text-white`}
                    >
                      <useCase.icon />
                    </div>

                    <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100">
                      {useCase.title}
                    </h3>

                    <p className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed">
                      {useCase.description}
                    </p>

                    <ul className="space-y-2 pt-2">
                      {useCase.features.map((feature) => (
                        <li
                          key={feature}
                          className="flex items-center text-sm text-gray-600 dark:text-gray-400"
                        >
                          <div className="mr-2 h-1 w-1 rounded-full bg-purple-600 dark:bg-purple-400" />
                          {feature}
                        </li>
                      ))}
                    </ul>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Integrations */}
        <section className="relative">
          <div className="absolute inset-0 z-[0] bg-purple-950/10 dark:bg-purple-950/10 bg-[radial-gradient(ellipse_20%_80%_at_50%_-20%,rgba(120,119,198,0.15),rgba(255,255,255,0))] dark:bg-[radial-gradient(ellipse_20%_80%_at_50%_-20%,rgba(120,119,198,0.3),rgba(255,255,255,0))]" />
          <div className="relative max-w-screen-xl z-10 mx-auto px-4 py-28 gap-12 md:px-8">
            <div className="space-y-5 max-w-3xl mx-auto text-center mb-12">
              <h1 className="text-sm text-gray-600 dark:text-gray-400 group font-geist mx-auto px-5 py-2 bg-gradient-to-tr from-zinc-300/20 via-gray-400/20 to-transparent dark:from-zinc-300/5 dark:via-gray-400/5 border-[2px] border-black/5 dark:border-white/5 rounded-xl w-fit">
                Integrations
                <ChevronRight className="inline w-4 h-4 ml-2 group-hover:translate-x-1 duration-300" />
              </h1>
              <h2 className="text-4xl pb-2 tracking-tighter font-geist bg-clip-text text-transparent mx-auto md:text-6xl bg-[linear-gradient(180deg,_#000_0%,_rgba(0,_0,_0,_0.75)_100%)] dark:bg-[linear-gradient(180deg,_#FFF_0%,_rgba(255,_255,_255,_0.00)_202.08%)]">
                Integrates With Your{" "}
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-purple-600 to-pink-500 dark:from-purple-300 dark:to-orange-200">
                  Team's Tools
                </span>
              </h2>
              <p className="max-w-2xl mx-auto text-gray-600 dark:text-gray-300">
                Connect your team's favorite tools and workflows. Set up once,
                everyone benefits.
              </p>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
              {integrations.map((integration) => (
                <motion.div
                  key={integration.name}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3 }}
                  viewport={{ once: true }}
                  className="group relative overflow-hidden rounded-xl border-[1px] border-black/5 dark:border-white/5 bg-gradient-to-tr from-zinc-300/20 via-gray-400/20 to-transparent dark:from-zinc-300/5 dark:via-gray-400/5 p-6 transition-all duration-300 hover:shadow-lg flex flex-col items-center justify-center text-center"
                >
                  <div className="absolute inset-0 bg-gradient-to-r from-purple-600/10 to-pink-500/10 dark:from-purple-300/10 dark:to-orange-200/10 opacity-0 transition-opacity duration-300 group-hover:opacity-100" />

                  <div className="relative">
                    <img
                      src={integration.logo}
                      alt={integration.name}
                      className="w-10 h-10 mb-3 mx-auto"
                    />
                    <h4 className="font-medium text-gray-900 dark:text-white text-sm mb-1">
                      {integration.name}
                    </h4>
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      {integration.description}
                    </p>
                  </div>
                </motion.div>
              ))}
            </div>

            <a
              href="/integrations"
              className="inline-flex items-center mt-8 text-blue-600 dark:text-blue-400 font-medium hover:underline"
            >
              View all integrations
              <ArrowRight className="ml-2 w-4 h-4" />
            </a>
          </div>
        </section>

        {/* Testimonials */}
        <section className="relative">
          <div className="absolute inset-0 z-[0] bg-purple-950/10 dark:bg-purple-950/10 bg-[radial-gradient(ellipse_20%_80%_at_50%_-20%,rgba(120,119,198,0.15),rgba(255,255,255,0))] dark:bg-[radial-gradient(ellipse_20%_80%_at_50%_-20%,rgba(120,119,198,0.3),rgba(255,255,255,0))]" />
          <div className="relative max-w-screen-xl z-10 mx-auto px-4 py-28 gap-12 md:px-8">
            <div className="space-y-5 max-w-3xl mx-auto text-center mb-16">
              <h1 className="text-sm text-gray-600 dark:text-gray-400 group font-geist mx-auto px-5 py-2 bg-gradient-to-tr from-zinc-300/20 via-gray-400/20 to-transparent dark:from-zinc-300/5 dark:via-gray-400/5 border-[2px] border-black/5 dark:border-white/5 rounded-xl w-fit">
                Testimonials
                <ChevronRight className="inline w-4 h-4 ml-2 group-hover:translate-x-1 duration-300" />
              </h1>
              <h2 className="text-4xl pb-2 tracking-tighter font-geist bg-clip-text text-transparent mx-auto md:text-6xl bg-[linear-gradient(180deg,_#000_0%,_rgba(0,_0,_0,_0.75)_100%)] dark:bg-[linear-gradient(180deg,_#FFF_0%,_rgba(255,_255,_255,_0.00)_202.08%)]">
                Loved by Teams{" "}
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-purple-600 to-pink-500 dark:from-purple-300 dark:to-orange-200">
                  Worldwide
                </span>
              </h2>
              <p className="max-w-2xl mx-auto text-gray-600 dark:text-gray-300">
                See what teams are saying about Boxia
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {testimonials.map((testimonial, index) => (
                <motion.div
                  key={testimonial.author}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  viewport={{ once: true }}
                  className="group relative overflow-hidden rounded-xl border-[1px] border-black/5 dark:border-white/5 bg-gradient-to-tr from-zinc-300/20 via-gray-400/20 to-transparent dark:from-zinc-300/5 dark:via-gray-400/5 p-8 transition-all duration-300 hover:shadow-xl"
                >
                  <div className="absolute inset-0 bg-gradient-to-r from-purple-600/10 to-pink-500/10 dark:from-purple-300/10 dark:to-orange-200/10 opacity-0 transition-opacity duration-300 group-hover:opacity-100" />

                  <div className="relative space-y-4">
                    <div className="flex items-center gap-1">
                      {[...Array(5)].map((_, i) => (
                        <Star
                          key={i}
                          className="w-4 h-4 fill-yellow-400 text-yellow-400"
                        />
                      ))}
                    </div>

                    <p className="text-sm text-gray-600 dark:text-gray-400 italic leading-relaxed">
                      "{testimonial.quote}"
                    </p>

                    <div className="flex items-center gap-3 pt-2">
                      <img
                        src={testimonial.avatar}
                        alt={testimonial.author}
                        className="w-12 h-12 rounded-full object-cover"
                      />
                      <div>
                        <div className="font-medium text-gray-900 dark:text-gray-100 text-sm">
                          {testimonial.author}
                        </div>
                        <div className="text-xs text-gray-500 dark:text-gray-400">
                          {testimonial.role}
                        </div>
                      </div>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Pricing Calculator */}
        <section className="relative">
          <div className="absolute inset-0 z-[0] bg-purple-950/10 dark:bg-purple-950/10 bg-[radial-gradient(ellipse_20%_80%_at_50%_-20%,rgba(120,119,198,0.15),rgba(255,255,255,0))] dark:bg-[radial-gradient(ellipse_20%_80%_at_50%_-20%,rgba(120,119,198,0.3),rgba(255,255,255,0))]" />
          <div className="relative max-w-screen-xl z-10 mx-auto px-4 py-28 gap-12 md:px-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
              className="space-y-5 max-w-3xl mx-auto text-center mb-16"
            >
              <h1 className="text-sm text-gray-600 dark:text-gray-400 group font-geist mx-auto px-5 py-2 bg-gradient-to-tr from-zinc-300/20 via-gray-400/20 to-transparent dark:from-zinc-300/5 dark:via-gray-400/5 border-[2px] border-black/5 dark:border-white/5 rounded-3xl w-fit">
                Pricing Calculator
                <ChevronRight className="inline w-4 h-4 ml-2 group-hover:translate-x-1 duration-300" />
              </h1>
              <h2 className="text-4xl tracking-tighter font-geist bg-clip-text text-transparent mx-auto md:text-6xl bg-[linear-gradient(180deg,_#000_0%,_rgba(0,_0,_0,_0.75)_100%)] dark:bg-[linear-gradient(180deg,_#FFF_0%,_rgba(255,_255,_255,_0.00)_202.08%)]">
                Calculate Your{" "}
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-purple-600 to-pink-500 dark:from-purple-300 dark:to-orange-200">
                  Team Cost
                </span>
              </h2>
              <p className="max-w-2xl mx-auto text-gray-600 dark:text-gray-300">
                Simple, transparent pricing - no hidden fees
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
              viewport={{ once: true }}
              className="max-w-4xl mx-auto"
            >
              <div className="relative overflow-hidden rounded-3xl border-[1px] border-black/5 dark:border-white/5 bg-gradient-to-tr from-zinc-300/20 via-gray-400/20 to-transparent dark:from-zinc-300/5 dark:via-gray-400/5 p-8 md:p-12 shadow-[inset_0_2px_4px_0_rgba(255,255,255,0.25),0_4px_12px_-4px_rgba(0,0,0,0.1)] dark:shadow-[inset_0_2px_4px_0_rgba(255,255,255,0.05),0_4px_12px_-4px_rgba(0,0,0,0.5)]">
                <div className="absolute inset-0 opacity-30 dark:opacity-20 bg-gradient-to-br from-purple-400/10 via-gray-400/5 to-transparent pointer-events-none" />

                <div className="relative z-10 space-y-8">
                  {/* Billing Toggle */}
                  <div className="flex items-center justify-center gap-4">
                    <span
                      className={`text-sm font-medium transition-colors duration-200 ${
                        !annualBilling
                          ? "text-gray-900 dark:text-white"
                          : "text-gray-500 dark:text-gray-400"
                      }`}
                    >
                      Monthly
                    </span>
                    <button
                      onClick={() => setAnnualBilling(!annualBilling)}
                      className={`relative inline-flex h-8 w-16 items-center rounded-full shadow-inner transition-all duration-200 ${
                        annualBilling
                          ? "bg-gradient-to-r from-purple-600 to-pink-600"
                          : "bg-gray-300 dark:bg-gray-600"
                      }`}
                    >
                      <span
                        className={`inline-block h-6 w-6 rounded-full bg-white shadow-lg transition-all duration-200 ${
                          annualBilling ? "translate-x-9" : "translate-x-1"
                        }`}
                      />
                    </button>
                    <span
                      className={`text-sm font-medium transition-colors duration-200 ${
                        annualBilling
                          ? "text-gray-900 dark:text-white"
                          : "text-gray-500 dark:text-gray-400"
                      }`}
                    >
                      Annual
                    </span>
                  </div>

                  {/* User Slider */}
                  <div className="space-y-6">
                    <div className="flex items-center justify-between">
                      <label className="text-lg font-semibold text-gray-900 dark:text-white">
                        Number of Team Members
                      </label>
                      <div className="flex items-center gap-3">
                        <span className="text-4xl font-bold bg-gradient-to-r from-purple-600 to-pink-600 dark:from-purple-400 dark:to-pink-400 bg-clip-text text-transparent">
                          {teamUsers}
                        </span>
                        <span className="text-sm text-gray-500 dark:text-gray-400">
                          users
                        </span>
                      </div>
                    </div>

                    <div className="relative">
                      <input
                        type="range"
                        min="3"
                        max="50"
                        value={teamUsers}
                        onChange={(e) => setTeamUsers(parseInt(e.target.value))}
                        className="w-full h-3 rounded-lg appearance-none cursor-pointer transition-all duration-200"
                        style={{
                          background: `linear-gradient(to right, rgb(147, 51, 234) 0%, rgb(236, 72, 153) ${((teamUsers - 3) / 47) * 100}%, rgb(229, 231, 235) ${((teamUsers - 3) / 47) * 100}%, rgb(229, 231, 235) 100%)`,
                        }}
                      />
                      <div className="flex justify-between text-sm text-gray-500 dark:text-gray-400 mt-3">
                        <span className="font-medium">3 users</span>
                        <span className="font-medium">50+ users</span>
                      </div>
                    </div>
                  </div>

                  {/* Price Display */}
                  <div className="relative overflow-hidden text-center py-10 px-8 rounded-2xl bg-gradient-to-tr from-zinc-300/20 via-purple-400/20 to-transparent dark:from-zinc-300/5 dark:via-purple-400/10 border-[1px] border-black/5 dark:border-white/5 shadow-[inset_0_2px_4px_0_rgba(255,255,255,0.25),0_4px_12px_-4px_rgba(0,0,0,0.1)] dark:shadow-[inset_0_2px_4px_0_rgba(255,255,255,0.05),0_4px_12px_-4px_rgba(0,0,0,0.5)]">
                    <div className="absolute inset-0 opacity-30 dark:opacity-20 bg-gradient-to-br from-purple-400/10 via-pink-400/5 to-transparent pointer-events-none" />

                    <div className="relative z-10 space-y-3">
                      <div className="text-sm font-medium text-gray-600 dark:text-gray-400 uppercase tracking-wider">
                        Total Team Cost
                      </div>
                      <div className="flex items-baseline justify-center gap-2">
                        <span className="text-6xl font-bold text-gray-900 dark:text-white">
                          ${calculateTeamCost()}
                        </span>
                        <span className="text-xl text-gray-600 dark:text-gray-400 font-medium">
                          / {annualBilling ? "year" : "month"}
                        </span>
                      </div>

                      <div className="text-sm text-gray-500 dark:text-gray-400">
                        ${(12.99).toFixed(2)} per user per month
                      </div>
                    </div>
                  </div>

                  {/* Features Grid */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {[
                      "Everything in Pro",
                      "Shared workspaces",
                      "Team analytics",
                      "Admin controls",
                      "Unlimited storage",
                      "Priority support",
                    ].map((feature) => (
                      <div
                        key={feature}
                        className="flex items-center gap-3 p-4 rounded-xl bg-gradient-to-tr from-zinc-300/10 via-gray-400/10 to-transparent dark:from-zinc-300/5 dark:via-gray-400/5 border-[1px] border-black/5 dark:border-white/5"
                      >
                        <CheckCircle className="w-5 h-5 text-green-500 dark:text-green-400 flex-shrink-0" />
                        <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                          {feature}
                        </span>
                      </div>
                    ))}
                  </div>

                  {/* CTA Buttons */}
                  <div className="flex flex-col sm:flex-row gap-4 pt-4">
                    <a
                      href="https://app.boxia.io/auth/signup/team"
                      className="flex-1 inline-flex items-center justify-center px-8 py-4 text-base font-medium rounded-xl bg-gradient-to-r from-purple-600 to-pink-500 dark:from-purple-300 dark:to-orange-200 text-white shadow-lg hover:shadow-xl transition-all duration-300"
                    >
                      Start Free Trial
                      <ArrowRight className="ml-2 w-5 h-5" />
                    </a>

                    <a
                      href="mailto:sales@boxia.io"
                      className="flex-1 inline-flex items-center justify-center px-8 py-4 text-base font-medium rounded-xl bg-gradient-to-tr from-zinc-300/20 via-gray-400/20 to-transparent dark:from-zinc-300/5 dark:via-gray-400/5 text-gray-900 dark:text-white border-[1px] border-black/5 dark:border-white/5 hover:bg-gradient-to-tr hover:from-zinc-300/30 hover:via-gray-400/30 hover:to-transparent dark:hover:from-zinc-300/10 dark:hover:via-gray-400/10 transition-all duration-200"
                    >
                      Contact Sales
                    </a>
                  </div>

                  <p className="text-center text-sm text-gray-500 dark:text-gray-400">
                    Need more than 50 users?{" "}
                    <a
                      href="mailto:sales@boxia.io"
                      className="text-purple-600 dark:text-purple-400 hover:underline font-medium"
                    >
                      Contact us
                    </a>{" "}
                    for Enterprise pricing
                  </p>
                </div>
              </div>
            </motion.div>
          </div>
        </section>

        {/* Final CTA */}
        <section className="relative">
          <div className="absolute inset-0 z-[0] bg-purple-950/10 dark:bg-purple-950/10 bg-[radial-gradient(ellipse_20%_80%_at_50%_-20%,rgba(120,119,198,0.15),rgba(255,255,255,0))] dark:bg-[radial-gradient(ellipse_20%_80%_at_50%_-20%,rgba(120,119,198,0.3),rgba(255,255,255,0))]" />
          <div className="relative max-w-screen-xl z-10 mx-auto px-4 py-28 gap-12 md:px-8">
            <div className="space-y-5 max-w-3xl mx-auto text-center">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
                viewport={{ once: true }}
              >
                <h1 className="text-sm text-gray-600 dark:text-gray-400 group font-geist mx-auto px-5 py-2 bg-gradient-to-tr from-zinc-300/20 via-gray-400/20 to-transparent dark:from-zinc-300/5 dark:via-gray-400/5 border-[2px] border-black/5 dark:border-white/5 rounded-xl w-fit">
                  Get Started
                  <ChevronRight className="inline w-4 h-4 ml-2 group-hover:translate-x-1 duration-300" />
                </h1>
                <h2 className="text-4xl mt-5 tracking-tighter font-geist bg-clip-text text-transparent mx-auto md:text-6xl bg-[linear-gradient(180deg,_#000_0%,_rgba(0,_0,_0,_0.75)_100%)] dark:bg-[linear-gradient(180deg,_#FFF_0%,_rgba(255,_255,_255,_0.00)_202.08%)]">
                  Ready to Transform Your{" "}
                  <span className="text-transparent bg-clip-text bg-gradient-to-r from-purple-600 to-pink-500 dark:from-purple-300 dark:to-orange-200">
                    Team's Productivity?
                  </span>
                </h2>
                <p className="max-w-2xl mx-auto mt-5 text-gray-600 dark:text-gray-300">
                  Join thousands of teams using Boxia to work smarter, faster,
                  and better together.
                </p>

                <div className="mt-10 flex flex-col items-center gap-4">
                  <a
                    href="https://app.boxia.io/auth/signup/team"
                    className="inline-flex items-center px-8 py-4 text-base font-medium rounded-xl bg-gradient-to-r from-purple-600 to-pink-500 dark:from-purple-300 dark:to-orange-200 text-white shadow-lg hover:shadow-xl transition-all duration-300"
                  >
                    Start Free 14 Day Trial
                    <ArrowRight className="ml-2 w-5 h-5" />
                  </a>
                  <span className="text-sm text-gray-500 dark:text-gray-400">
                    Cancel anytime • Set up in minutes
                  </span>
                </div>
              </motion.div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default TeamsPage;
